import React from "react";
import classnames from "classnames";
import {
    DisassociateIcon,
    PencilIcon,
    SettingsIcon,
    ShieldExclamationIcon,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next/";
import { useSnapshot } from "valtio";
import { Tooltip } from "@mui/material";

import store, {
    setIsModalDissociateOpened,
    setSelectedApplicationSco,
} from "./store";
import {
    store as appStore,
    setIsActionsClicked,
    setIsEdited,
    setActions,
} from "../store";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/store";
import { openPosConfigModal } from "./PosConfigurationModal/posConfigSlice";
import { PosProjectRow } from "./PosConfigurationModal/constants";
import { store as userStore } from "../../../../components/VerticalLayout/store";

import TimePastSvgIcon from "@components/Common/SvgIcons/TimePastSvgIcon";
import ConsultSvgIcon from "@components/Common/SvgIcons/ConsultSvgIcon";
import { toast } from "react-toastify";

type CustomIconsProps = {
    e: { selectedRow: PosProjectRow };
    setSelectedRows: (value: PosProjectRow | PosProjectRow[]) => void;
    setEditingRow: (value: PosProjectRow) => void;
    setIsAddProject: (value: boolean) => void;
    isProjectArchived: boolean;
    setIsHistoryModalOpened: (value: boolean) => void;
    isHistoryModalOpened: boolean;
};

export function CustomIcons({
    e,
    setSelectedRows,
    setEditingRow,
    setIsAddProject,
    isProjectArchived,
    setIsHistoryModalOpened,
    isHistoryModalOpened,
}: CustomIconsProps) {
    const { t } = useTranslation();
    const reduxDispatch = useDispatch<AppDispatch>();
    const { isModalDissociateOpened, modify } = useSnapshot(store);
    const { isActionsClicked, isEdited } = useSnapshot(appStore);
    const { franchiseID, shopID, userID, operatorID, oneShop } = useSnapshot(
        userStore
    );

    const handleEditClick = () => {
        if (isEdited === true) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setIsAddProject(true);
                setEditingRow(e.selectedRow);
            });
        } else {
            setIsAddProject(true);
            setEditingRow(e.selectedRow);
        }
    };

    const handleHistoryClick = () => {
        if (isEdited === true) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setIsHistoryModalOpened(!isHistoryModalOpened);
                setSelectedApplicationSco([
                    {
                        [e.selectedRow.shopId || ""]:
                            e.selectedRow.ProjectId || "",
                    },
                ]);
                setSelectedRows([e.selectedRow]);
            });
        } else {
            setIsHistoryModalOpened(!isHistoryModalOpened);
            setSelectedApplicationSco([
                {
                    [e.selectedRow.shopId || ""]: e.selectedRow.ProjectId || "",
                },
            ]);
            setSelectedRows([e.selectedRow]);
        }
    };

    if (
        e.selectedRow?.subRows !== undefined &&
        e.selectedRow.key_project !== undefined
    ) {
        return (
            <div className="d-flex justify-content-between mt-2 ml-5">
                <div>
                    {modify === true && (
                        <Tooltip title={t("Modify project")}>
                            <div>
                                <PencilIcon
                                    onClick={handleEditClick}
                                    height={25}
                                    width={25}
                                    style={{ cursor: "pointer" }}
                                    className={`${classnames(
                                        "pointer__clz cmn_icn_FRedit",
                                        {
                                            "not-allowed-icon__clz": isProjectArchived,
                                        }
                                    )}`}
                                />
                            </div>
                        </Tooltip>
                    )}
                </div>
                <div>
                    <Tooltip title={t("Historical")}>
                        <div>
                            <TimePastSvgIcon
                                onClick={handleHistoryClick}
                                className="pointer__clz ml-2 cmn_icn_FRhistory"
                            />
                        </div>
                    </Tooltip>
                </div>
            </div>
        );
    }

    if (
        e.selectedRow[t("Designation")] !== "" &&
        e.selectedRow.id_boutique !== undefined
    ) {
        return (
            <div className="mt-3">
                {modify === true && (
                    <Tooltip title={t("Unlink the store from the Template")}>
                        <div>
                            <DisassociateIcon
                                height={40}
                                width={35}
                                fill="white"
                                style={{ position: "relative", bottom: "10px" }}
                                className={`${classnames("pointer__clz ml-2", {
                                    "not-allowed-icon__clz": isProjectArchived,
                                })}`}
                                onClick={() => {
                                    if (e.selectedRow.Status === "Publié") {
                                        toast.error(
                                            `${t(
                                                "Unable to unlink a store from a published Template."
                                            )!}`,
                                            {
                                                position:
                                                    toast.POSITION.TOP_CENTER,
                                                autoClose: 2000,
                                                theme: "colored",
                                                closeOnClick: true,
                                                pauseOnHover: true,
                                                draggable: true,
                                                icon: (
                                                    <ShieldExclamationIcon
                                                        width={25}
                                                        height={25}
                                                        fill="white"
                                                    />
                                                ),
                                            }
                                        );
                                    } else {
                                        setSelectedRows([e.selectedRow]);
                                        setIsModalDissociateOpened(
                                            !isModalDissociateOpened
                                        );
                                    }
                                }}
                            />
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    }

    // Application level: settings icon to open config modal
    if (e.selectedRow?.appId !== undefined) {
        const openModal = (consultation: boolean) => {
            setSelectedRows(e.selectedRow);
            reduxDispatch(
                openPosConfigModal({
                    row: e.selectedRow,
                    level: "application",
                    franchiseId: String(franchiseID),
                    shopId: String(e.selectedRow.shopId || shopID),
                    userId: String(userID),
                    operatorId: String(operatorID),
                    isConsultation: consultation,
                })
            );
        };

        return (
            <div className="d-flex justify-content-center align-items-center h-100 gap-2">
                <Tooltip title={t("configure")}>
                    <div>
                        <SettingsIcon
                            height={20}
                            width={20}
                            style={{
                                cursor: oneShop ? "not-allowed" : "pointer",
                                opacity: oneShop ? 0.4 : 1,
                            }}
                            className={classnames(
                                "pointer__clz cmn_icn_configuration",
                                { "not-allowed-icon__clz": oneShop }
                            )}
                            onClick={() => {
                                if (oneShop) return;
                                openModal(false);
                            }}
                            data-testid="pos-app-config-icon"
                        />
                    </div>
                </Tooltip>
                {oneShop && (
                    <Tooltip title={t("To consult").toString()}>
                        <div>
                            <ConsultSvgIcon
                                height={30}
                                width={30}
                                style={{ cursor: "pointer" }}
                                className="pointer__clz cgt_icn_consult"
                                onClick={() => openModal(true)}
                                data-testid="pos-app-consult-icon"
                            />
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    }

    return null;
}
